import type { ResponsibleLimits } from '@tira/protocol';
import { useEffect, useState, type JSX } from 'react';
import { Button } from '../components/Button.js';
import { Icon } from '../components/Icon.js';
import { Sheet } from '../components/Sheet.js';
import { setLang, useLang, useT, type CopyKey, type T } from '../i18n/index.js';
import { api, setToken } from '../net/api.js';
import { useApp } from '../state/store.js';
import { currentPrefs, readPrefs, savePrefs, type LocalPrefs } from './lib/prefs.js';
import { sfx, unlockSound } from '../sound/sfx.js';
import { Screen } from './parts/Screen.js';
import './Settings.css';

/** Session-reminder cadences, in minutes. 0 = off. */
const REMINDERS = [0, 30, 60, 120] as const;
/** Cool-off is a pause; self-exclusion is a door. Both are irreversible once set. */
const COOL_OFFS: { label: CopyKey; hours: number }[] = [
  { label: 'account.dur.24h', hours: 24 },
  { label: 'account.dur.3d', hours: 72 },
  { label: 'account.dur.7d', hours: 168 },
];
const EXCLUSIONS: { label: CopyKey; hours: number }[] = [
  { label: 'account.dur.30d', hours: 720 },
  { label: 'account.dur.90d', hours: 2_160 },
  { label: 'account.dur.180d', hours: 4_320 },
];

interface Confirm {
  title: string;
  body: string;
  hard: string;
  cta: string;
  run: () => Promise<unknown>;
}

/** Cool-off / self-exclusion countdowns, in the coarse unit that reads best. */
function until(iso: string | null, t: T): string | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const hours = Math.ceil(ms / 3_600_000);
  if (hours < 48) return t('account.until.hours', { n: hours });
  return t('account.until.days', { n: Math.ceil(hours / 24) });
}

/**
 * S15 — settings. Responsible play is the first panel on the screen, not a row
 * at the bottom: PRD §11.3 ships these in v1 and §13 wants them reachable in
 * one tap from every store surface.
 */
export function Settings(): JSX.Element {
  const me = useApp((s) => s.me);
  const refreshMe = useApp((s) => s.refreshMe);
  const setToast = useApp((s) => s.setToast);
  const t = useT();
  const lang = useLang();

  const [limits, setLimits] = useState<ResponsibleLimits | null>(me?.limits ?? null);
  const [capDraft, setCapDraft] = useState(String(me?.limits.dailySpendCapPhp ?? 0));
  const [prefs, setPrefs] = useState<LocalPrefs>(readPrefs);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (me) {
      setLimits(me.limits);
      setCapDraft(String(me.limits.dailySpendCapPhp));
    }
  }, [me]);

  const push = (patch: Partial<ResponsibleLimits>, said: string) => {
    setBusy(true);
    return api
      .setLimits(patch)
      .then((next) => {
        setLimits(next);
        setBusy(false);
        setToast(said, 'info');
        void refreshMe();
      })
      .catch((e: Error) => {
        setBusy(false);
        setToast(t('account.save.failed', { err: e.message }), 'warn');
      });
  };

  /*
   * Spread the live prefs rather than this screen's copy of them: the language
   * buttons write through setLang(), so a stale local snapshot would undo that
   * choice the next time somebody flips a toggle.
   */
  const setPref = (patch: Partial<LocalPrefs>) => {
    const next = { ...currentPrefs(), ...patch };
    setPrefs(next);
    savePrefs(next);
  };

  const coolOff = until(limits?.coolOffUntil ?? null, t);
  const excluded = until(limits?.selfExcludedUntil ?? null, t);

  return (
    <Screen title={t('account.settings.title')} sub={t('account.settings.sub')}>
      <section className="s-panel settings__care">
        <div className="settings__careHead">
          <Icon name="responsible" size={26} />
          <div>
            <h2>{t('account.care.title')}</h2>
            <p>{t('account.care.body')}</p>
          </div>
        </div>

        {excluded ? (
          <p className="settings__active">{t('account.care.excluded', { until: excluded })}</p>
        ) : coolOff ? (
          <p className="settings__active">{t('account.care.cooloff', { until: coolOff })}</p>
        ) : null}

        <div className="settings__limit">
          <b>{t('account.reminder.label')}</b>
          <span className="s-note">{t('account.reminder.note')}</span>
          <div className="s-segment">
            {REMINDERS.map((m) => (
              <button
                key={m}
                aria-pressed={(limits?.sessionReminderMin ?? 0) === m}
                disabled={busy}
                onClick={() =>
                  void push(
                    { sessionReminderMin: m },
                    m === 0
                      ? t('account.reminder.saved.off')
                      : t('account.reminder.saved', { n: m }),
                  )
                }
              >
                {m === 0 ? t('account.reminder.off') : t('account.reminder.min', { n: m })}
              </button>
            ))}
          </div>
        </div>

        <div className="settings__limit">
          <b>{t('account.cap.label')}</b>
          <span className="s-note">{t('account.cap.note')}</span>
          <div className="settings__capRow">
            <div className="s-field">
              <label htmlFor="cap">{t('account.cap.field')}</label>
              <input
                id="cap"
                className="s-input"
                type="number"
                min={0}
                max={100000}
                step={50}
                value={capDraft}
                onChange={(e) => setCapDraft(e.currentTarget.value)}
              />
            </div>
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => {
                const v = Math.max(0, Math.min(100_000, Math.trunc(Number(capDraft) || 0)));
                setCapDraft(String(v));
                void push(
                  { dailySpendCapPhp: v },
                  v === 0 ? t('account.cap.saved.off') : t('account.cap.saved', { v }),
                );
              }}
            >
              {t('account.cap.save')}
            </Button>
          </div>
        </div>

        <div className="settings__limit">
          <b>{t('account.cooloff.label')}</b>
          <span className="s-note">{t('account.cooloff.note')}</span>
          <div className="s-actions">
            {COOL_OFFS.map((c) => (
              <Button
                key={c.hours}
                variant="secondary"
                disabled={busy || excluded !== null}
                onClick={() =>
                  setConfirm({
                    title: t('account.cooloff.title', { dur: t(c.label) }),
                    body: t('account.cooloff.body', { dur: t(c.label) }),
                    hard: t('account.cooloff.hard'),
                    cta: t('account.cooloff.cta', { dur: t(c.label) }),
                    run: () =>
                      push(
                        { coolOffUntil: new Date(Date.now() + c.hours * 3_600_000).toISOString() },
                        t('account.cooloff.done', { dur: t(c.label) }),
                      ),
                  })
                }
              >
                {t(c.label)}
              </Button>
            ))}
          </div>
        </div>

        <div className="settings__limit">
          <b>{t('account.exclude.label')}</b>
          <span className="s-note">{t('account.exclude.note')}</span>
          <div className="s-actions">
            {EXCLUSIONS.map((c) => (
              <Button
                key={c.hours}
                variant="destructive"
                disabled={busy}
                onClick={() =>
                  setConfirm({
                    title: t('account.exclude.title', { dur: t(c.label) }),
                    body: t('account.exclude.body', { dur: t(c.label) }),
                    hard: t('account.exclude.hard'),
                    cta: t('account.exclude.cta', { dur: t(c.label) }),
                    run: () =>
                      push(
                        { selfExcludedUntil: new Date(Date.now() + c.hours * 3_600_000).toISOString() },
                        t('account.exclude.done', { dur: t(c.label) }),
                      ),
                  })
                }
              >
                {t(c.label)}
              </Button>
            ))}
          </div>
        </div>

        <div className="settings__limit">
          <label className="s-toggle">
            <input
              type="checkbox"
              checked={limits?.muteTableTalk ?? false}
              disabled={busy}
              onChange={(e) =>
                void push(
                  { muteTableTalk: e.currentTarget.checked },
                  e.currentTarget.checked ? t('account.mute.on') : t('account.mute.off'),
                )
              }
            />
            <span className="s-row__main">
              <b>{t('account.mute.label')}</b>
              <span>{t('account.mute.note')}</span>
            </span>
          </label>
        </div>

        <p className="s-disclosure">
          <Icon name="chip" size={16} />
          <span>{t('account.disclosure')}</span>
        </p>
      </section>

      <section className="s-panel">
        <div className="s-panel__head">
          <h2>{t('account.look.title')}</h2>
          <p>{t('account.look.sub')}</p>
        </div>

        <div className="settings__limit">
          <b>{t('account.lang.label')}</b>
          <div className="s-segment">
            <button aria-pressed={lang === 'tl'} onClick={() => setLang('tl')}>
              Taglish
            </button>
            <button aria-pressed={lang === 'en'} onClick={() => setLang('en')}>
              English
            </button>
          </div>
          <span className="s-note">{t('account.lang.note')}</span>
        </div>

        <div className="settings__limit">
          <b>{t('account.view.label')}</b>
          <div className="s-segment">
            <button
              aria-pressed={prefs.tableView === 'room'}
              onClick={() => setPref({ tableView: 'room' })}
            >
              2.5D
            </button>
            <button
              aria-pressed={prefs.tableView === 'top'}
              onClick={() => setPref({ tableView: 'top' })}
            >
              {t('account.view.top')}
            </button>
          </div>
          <span className="s-note">{t('account.view.note')}</span>
        </div>

        <div className="settings__limit">
          <b>{t('account.sound.label')}</b>
          <div className="s-segment">
            {(['off', 'mahina', 'katamtaman', 'malakas'] as const).map((lv) => (
              <button
                key={lv}
                aria-pressed={prefs.sfx === lv}
                onClick={() => {
                  setPref({ sfx: lv });
                  // The tap auditions the level it just set: a volume picked
                  // deaf is a guess, and this very tap is also the gesture
                  // that unlocks the context on a first visit.
                  unlockSound();
                  sfx('tile');
                }}
              >
                {t(`account.sound.${lv}`)}
              </button>
            ))}
          </div>
          <span className="s-note">{t('account.sound.note')}</span>
        </div>

        <div className="settings__limit">
          <label className="s-toggle">
            <input
              type="checkbox"
              checked={prefs.reducedMotion}
              onChange={(e) => setPref({ reducedMotion: e.currentTarget.checked })}
            />
            <span className="s-row__main">
              <b>{t('account.motion.label')}</b>
              <span>{t('account.motion.note')}</span>
            </span>
          </label>
        </div>

        <div className="settings__limit">
          <label className="s-toggle">
            <input
              type="checkbox"
              checked={prefs.colourblind}
              onChange={(e) => setPref({ colourblind: e.currentTarget.checked })}
            />
            <span className="s-row__main">
              <b>{t('account.cb.label')}</b>
              <span>{t('account.cb.note')}</span>
            </span>
          </label>
        </div>
      </section>

      <section className="s-panel settings__danger">
        <div className="s-panel__head">
          <h2>{t('account.acct.title')}</h2>
          <p>
            {me?.handle ?? t('account.acct.guest')}
            {me?.email ? ` · ${me.email}` : ` · ${t('account.acct.noemail')}`}
          </p>
        </div>
        <p className="s-note">{t('account.acct.note')}</p>
        <div className="s-actions">
          <Button
            variant="destructive"
            onClick={() =>
              setConfirm({
                title: t('account.delete.cta'),
                body: t('account.delete.body'),
                hard: t('account.delete.hard'),
                cta: t('account.delete.yes'),
                run: () =>
                  api.deleteAccount().then(() => {
                    setToken(null);
                    location.reload();
                  }),
              })
            }
          >
            {t('account.delete.cta')}
          </Button>
        </div>
      </section>

      <Sheet open={confirm !== null} onClose={() => setConfirm(null)} title={confirm?.title ?? ''}>
        {confirm ? (
          <div className="confirm">
            <p className="confirm__body">{confirm.body}</p>
            <p className="confirm__hard">
              <Icon name="responsible" size={18} />
              <span>{confirm.hard}</span>
            </p>
            <div className="s-actions">
              <Button
                variant="destructive"
                disabled={busy}
                onClick={() => {
                  const run = confirm.run;
                  setConfirm(null);
                  void run().catch((e: Error) =>
                    setToast(t('account.action.failed', { err: e.message }), 'warn'),
                  );
                }}
              >
                {confirm.cta}
              </Button>
              <Button variant="ghost" onClick={() => setConfirm(null)}>
                {t('account.confirm.no')}
              </Button>
            </div>
          </div>
        ) : null}
      </Sheet>
    </Screen>
  );
}
